/**
 * Color Design Tokens
 *
 * Color-specific token handling that uses the general token utilities.
 * This matches the SCSS core/colors/_tokens.scss implementation.
 */

import { createTokenExtractor } from '../core/tokens';
import { type ColorName } from '../values/colors';

/**
 * Color token extractor with type safety
 *
 * Matches SCSS: color($color-name)
 *
 * @param colorName - The name of the color token (must be a valid ColorName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the color token
 */
export const color = createTokenExtractor<ColorName>('color');

/**
 * Foreground color token extractor with type safety
 *
 * Matches SCSS: foreground-color-for($color-name)
 *
 * @param colorName - The name of the color token (must be a valid ColorName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the foreground color token
 */
export const foregroundColorFor = createTokenExtractor<ColorName>('color-foreground');

/**
 * Example usage:
 *
 * ```typescript
 * import { color, foregroundColorFor } from './colors';
 *
 * const bgColor = color('functional-default'); // ✅ Valid color name
 * const textColor = foregroundColorFor('base-dark'); // ✅ Valid color name
 *
 * // TypeScript will show error for invalid color names:
 * // const invalidColor = color('invalid-color'); // ❌ Type error
 * ```
 */
